/**
 * Browser GraphQL client factory — the CLIENT-SAFE half of
 * `@/lib/graphql/graphql-clients`.
 *
 * Split out because `graphql-clients.ts` imports `@/lib/audit/attribution-context`,
 * which imports `node:async_hooks`. `src/lib/providers.tsx` (the root
 * `ApolloProvider`, a `"use client"` module mounted on every page) needs the
 * browser factory, and reaching it THROUGH `graphql-clients.ts` pulled
 * `node:async_hooks` into the browser graph. Turbopack refuses that at build
 * time ("the chunking context (unknown) does not support external modules
 * (request: node:async_hooks)") — `tsc` and jest cannot see it. Keep this file
 * free of server-only imports; `tests/graphql/browser-graphql-client-boundary.test.ts`
 * walks its import graph to hold that line.
 */

import { createBrowserGraphQLClient as sharedCreateBrowserGraphQLClient } from "@west-tennessee-consulting/supabase-graphql-kit";
import { getBrowserSupabaseClient } from "@west-tennessee-consulting/supabase";
import { logger } from "@/lib/logger";

/**
 * Hub's `DeclareDefaultOptions` augmentation requires exactly this shape on
 * every `ApolloClient` in the program — see
 * `apps/hub/types/apollo-client-augmentation.d.ts`.
 */
export const hubDefaultOptions = {
  watchQuery: { errorPolicy: "all" as const },
  query: { errorPolicy: "all" as const },
};

/**
 * In-memory cache of the current Supabase access token, shared by every
 * `createClientGraphQLClient()` instance in this process.
 *
 * Awaiting `supabase.auth.getSession()` on EVERY GraphQL operation was a
 * per-request tax every CRM list query paid on the hot path, so the token is
 * cached with its own expiry and re-read from the session once it is within
 * `EXPIRY_SKEW_MS` of expiring. Never serve a cached token past that point: the
 * old cache was only cleared by `onAuthStateChange`, so a missed or reordered
 * event left an expired token in place.
 */
interface CachedToken {
  accessToken: string;
  /** Epoch ms; 0 when the session carried no `expires_at`. */
  expiresAtMs: number;
}

const EXPIRY_SKEW_MS = 30_000;

let cachedToken: CachedToken | null = null;
let authListenerAttached = false;

function toCachedToken(
  session: { access_token?: string | null; expires_at?: number | null } | null,
): CachedToken | null {
  if (!session?.access_token) return null;
  return {
    accessToken: session.access_token,
    expiresAtMs: session.expires_at ? session.expires_at * 1000 : 0,
  };
}

function isFresh(token: CachedToken): boolean {
  return token.expiresAtMs === 0 || token.expiresAtMs - Date.now() > EXPIRY_SKEW_MS;
}

/**
 * Create a client-side GraphQL client that uses the user's authentication token
 * This respects RLS policies and should be used for all user-facing operations.
 *
 * The token comes from the SAME cookie-backed `@supabase/ssr` browser singleton
 * every other Hub browser call uses (`getBrowserSupabaseClient`). It used to
 * construct a plain `@supabase/supabase-js` client per call, which reads
 * localStorage - not the cookies the session actually lives in - so a miss was
 * silent: the shared kit falls back to `Bearer <anon key>`, the request ran as
 * `anon`, and the user saw `permission denied for function is_employee` in an
 * "Error loading details" modal (prod 2026-10-04: 9 of 40 browser GraphQL calls
 * went out as anon while the REST path showed none).
 */
export function createClientGraphQLClient() {
  return sharedCreateBrowserGraphQLClient(
    {
      url: process.env.NEXT_PUBLIC_SUPABASE_URL!,
      anonKey: process.env.NEXT_PUBLIC_SUPABASE_ANON_KEY!,
      getAccessToken: async () => {
        // Browser-only - never attempt a session lookup during SSR.
        if (typeof window === "undefined") return null;
        try {
          const supabase = getBrowserSupabaseClient();
          if (!authListenerAttached) {
            authListenerAttached = true;
            supabase.auth.onAuthStateChange((_event, session) => {
              cachedToken = toCachedToken(session);
            });
          }

          // Fast path: a cached token that is not about to expire.
          if (cachedToken && isFresh(cachedToken)) return cachedToken.accessToken;

          // Cold or stale path: `getSession()` refreshes an expiring session
          // itself, so this returns a usable token whenever one can exist.
          const { data, error } = await supabase.auth.getSession();
          if (error) logger.error("GraphQL Auth Error", error);
          cachedToken = toCachedToken(data.session);
          if (!cachedToken) {
            // No session: this request goes out as anon (the kit's fallback).
            // Say so, because RLS then answers with a misleading
            // "permission denied for function ..." instead of an auth error.
            logger.warn("GraphQL request sent without a session; running as anon");
          }
          return cachedToken?.accessToken ?? null;
        } catch (authError) {
          logger.error("Failed to get auth session", authError);
          return null;
        }
      },
    },
    {
      defaultOptions: hubDefaultOptions,
    },
  );
}
